import classNames from 'classnames'
import DetectRTC from 'detectrtc'
import { ReactNode, useCallback } from 'react'
import styled from 'styled-components'

import { Language } from '../../constants'
import { useRoute } from '../../hooks/useRoute'
import { audio } from '../../lib/audio'
import { OpenMicrophonesModal } from '../../services/event-bus/events'
import { Visit, VisitType } from '../../services/models/Visit.model'
import { useBillingContext } from '../Billing/hooks/useBillingContext'
import { useVisitsContext } from '../Visits/hooks/useVisitsContext'


interface Props {
    className?: string
    startRecordingOnLoad?: boolean
    templateId?: string
    customTemplateId?: string
    languageSource?: Language
    languageTarget?: Language
    onBeforeCreate?(): void
    onCreated?(): void
    childNode(_isLoading: boolean): ReactNode
    type: VisitType
}

const Styles = styled.div`
    display: inline-block;
`

export const CreateVisit = ({
    className,
    startRecordingOnLoad,
    onBeforeCreate,
    onCreated,
    childNode,
    templateId,
    customTemplateId,
    languageSource,
    languageTarget,
    type
}: Props) => {
    const { navVisitRecord, navDictationRecord } = useRoute()
    const { createVisit, isCreateVisitLoading } = useVisitsContext().visits
    const { showPreview } = useVisitsContext().view
    const { billingUsages, increaseVisitsCreated, getBillingUsages } = useBillingContext().billingUsages

    const onClick = useCallback(
        async (event: any) => {
            event.stopPropagation()

            // Check microphone permissons
            const micPermisson = DetectRTC.isWebsiteHasMicrophonePermissions

            if (!micPermisson) {
                try {
                    await audio.getStream()
                } catch (error) {
                    OpenMicrophonesModal.emit({})
                    return
                }
            }

            // Perform some action before creating visit
            onBeforeCreate?.()

            // Create Visit
            createVisit(templateId, customTemplateId, type)
                .then((newVisit: Visit | null) => {
                    if (!newVisit) {
                        return
                    }

                    // Navigate to record page
                    if(newVisit.type === 'dictation'){
                        navDictationRecord(newVisit._id, newVisit, startRecordingOnLoad)
                    }else{
                        navVisitRecord(newVisit._id, newVisit, startRecordingOnLoad, templateId, customTemplateId, languageSource, languageTarget)
                    }
                    showPreview()

                    if (billingUsages) {
                        // Update billing usages
                        increaseVisitsCreated()
                    } else {
                        getBillingUsages()
                    }

                    if (onCreated) {
                        onCreated()
                    }
                })
                .catch((error) => console.error(error))
        },
        [
            createVisit,
            billingUsages,
            startRecordingOnLoad,
            increaseVisitsCreated,
            navVisitRecord,
            showPreview,
            onBeforeCreate,
            onCreated,
            templateId,
            customTemplateId,
            languageSource,
            languageTarget,
            getBillingUsages,
            type,
            navDictationRecord
        ]
    )

    return (
        <Styles className={classNames(className)} onClick={onClick}>
            {childNode(isCreateVisitLoading)}
        </Styles>
    )
}
